import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageForm from "components/pageForm";
import Indicator from "pages/createMission/indicator";
import FooterButton from "pages/createMission/footer.button";
import Button from "components/button";
import CreateMissionSubNav from "components/subNav/createMission.subNav";
import FormsCreate from "pages/createMission/formsCreate";
import UseContext from "hooks/useContext";
import ApiDatabase from "server";
import { toast } from "react-toastify";
import useModal from "../components/modal/useModal";
import { dateToEpoch, dateToIsoString } from "../helper/helper";
import { toToolNames } from "pages/missionTemplates/utils/templateTools";
import {
    MISSION_END_PAGE,
    MISSION_FIRST_PAGE,
    MISSION_STEP,
    MISSION_STEPS_COUNT,
    MISSION_VALID_PAGES,
    getMissionStepIndex,
    getMissionStepPage,
} from "pages/createMission/utils/missionSteps";
import PerimeterMissionStep from "components/perimeterMissionStep/PerimeterMissionStep";
import {
    getDefaultPerimeterChoice,
    getMissionPerimeterErrorKey,
    isPerimeterChoiceAvailable,
    isPerimeterStepShown,
    toPerimeterIdPayload,
} from "components/perimeterMissionStep/perimeterChoice";

const DETAILS_PAGE = getMissionStepPage(MISSION_STEP.DETAILS);

const hasLocation = (location) => {
    if (!location) return false;
    if (typeof location === "string") return location.trim() !== "";
    return Object.keys(location).length > 0;
};

const resetMissionData = {
    activity: "",
    qualification: "",
    tools: [],
    licences: [],
    languages: [],
    contact_name: "",
    contact_poste: "",
    location: "",
    dateStart: "",
    dateEnd: "",
    schedule: [],
    isScheduleCheck: true,
    isConfirmedSchedule: false,
    isScheduleRequired: false,
    users_invite: [],
    motif: "",
    other: "",
    other_interim: "",
    salary: "",
};

const nextPageFrom = (page, validPages) => {
    const idx = validPages.indexOf(page);
    return validPages[idx + 1] ?? page;
};

const prevPageFrom = (page, validPages) => {
    const idx = validPages.indexOf(page);
    if (idx <= 0) return page;
    return validPages[idx - 1] ?? page;
};

const CreateOfMission = () => {
    const navigate = useNavigate();
    const { toggle } = useModal();
    const {
        page,
        setPage,
        data,
        setData,
        companyData,
        isDateValid,
        selectedMissionTemplate,
        setSelectedMissionTemplate,
        language,
    } = UseContext();

    const [isSubmitLoading, setIsSubmitLoading] = React.useState(false);
    const [perimeterOptions, setPerimeterOptions] = React.useState(null);
    const [perimeterChoice, setPerimeterChoice] = React.useState("");
    const [perimeterError, setPerimeterError] = React.useState("");
    const isMountedRef = React.useRef(true);
    const validPages = MISSION_VALID_PAGES;
    const firstPage = MISSION_FIRST_PAGE;
    // The perimeter block of the Details step only shows when the company has
    // an active perimeter; without it, Details is left as it always was.
    const isPerimeterShown = isPerimeterStepShown(perimeterOptions);
    const canLeaveDetails =
        !isPerimeterShown ||
        isPerimeterChoiceAvailable(perimeterOptions, perimeterChoice);

    // Any failure leaves the block hidden: the mission is then created as before
    // perimeters, and the api applies its own rule for restricted members.
    const loadPerimeterOptions = React.useCallback((onLoaded) => {
        ApiDatabase.getPerimeterOptions(
            {},
            (res) => {
                if (!isMountedRef.current) return;
                const options = res?.data;
                const isShown = isPerimeterStepShown(options);
                setPerimeterOptions(isShown ? options : null);
                setPerimeterChoice((current) =>
                    isPerimeterChoiceAvailable(options, current)
                        ? current
                        : isShown
                        ? getDefaultPerimeterChoice(options)
                        : ""
                );
                onLoaded?.(isShown);
            },
            (err) => {
                if (!isMountedRef.current) return;
                console.error(err);
                onLoaded?.(null);
            }
        );
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        loadPerimeterOptions();
        return () => {
            isMountedRef.current = false;
        };
    }, [loadPerimeterOptions]);

    useEffect(() => {
        if (!validPages.includes(page)) {
            setPage(firstPage);
        }
    }, [firstPage, page, setPage, validPages]);

    useEffect(() => {
        if (page === MISSION_END_PAGE) return;
        if (!selectedMissionTemplate?._id) {
            navigate("/missionTemplates", { replace: true });
            toast.info(
                "Choisissez une fiche de poste depuis la liste pour démarrer une mission."
            );
        }
    }, [selectedMissionTemplate, navigate, page]);

    // Hydrate `data` depuis la fiche sélectionnée.
    // The template's instructions only prefill the Details step: the mission sends what is typed there.
    useEffect(() => {
        if (!selectedMissionTemplate) return;
        const t = selectedMissionTemplate;
        setData((prev) => ({
            ...prev,
            activity: t.activity || "",
            qualification: t.title || t.qualification || "",
            tools: toToolNames(t.tools),
            licences: t.licences || [],
            languages: t.language || [],
            contact_name: t.contact?.name || "",
            contact_poste: t.contact?.poste || "",
            location: t.location || "",
            other: t.other || "",
            other_interim: t.other_interim || "",
            salary:
                typeof t.salary?.wage_per_hour === "number"
                    ? String(t.salary.wage_per_hour)
                    : "",
        }));
    }, [selectedMissionTemplate, setData]);

    const handlePrev = () => {
        if (page === firstPage) {
            navigate("/missionTemplates");
            return;
        }
        setPage(prevPageFrom(page, validPages));
    };
    const handleNext = () => setPage(nextPageFrom(page, validPages));

    // The refusal means the options shown are outdated (perimeter archived,
    // membership changed): reload them before sending the user back to Details.
    const handlePerimeterRefusal = (message) => {
        loadPerimeterOptions((isShown) => {
            setIsSubmitLoading(false);
            if (isShown === false) {
                // No active perimeter left: the block is gone, a new
                // confirmation creates the mission without perimeter.
                toast.error(message);
                return;
            }
            if (isShown === null && !isPerimeterShown) {
                toast.error(message);
                return;
            }
            setPerimeterError(message);
            setPage(DETAILS_PAGE);
        });
    };

    const handlePerimeterChange = (choice) => {
        setPerimeterChoice(choice);
        setPerimeterError("");
    };

    const handleSubmit = (e) => {
        setIsSubmitLoading(true);
        e.preventDefault();
        if (!selectedMissionTemplate?._id) {
            setIsSubmitLoading(false);
            toast.error("Veuillez sélectionner une fiche de poste");
            return;
        }
        // The options may have arrived after the user went past Details.
        if (!canLeaveDetails) {
            setIsSubmitLoading(false);
            setPerimeterError(
                language.getValue("perimeters.missionStep.errors.required")
            );
            setPage(DETAILS_PAGE);
            return;
        }
        const idCompany = companyData._id;

        if (data.schedule.length > 0) {
            for (let i = 0; i < data.schedule.length; i++) {
                if (data.schedule[i].days.length > 0) {
                    for (let j = 0; j < data.schedule[i].days.length; j++) {
                        if (data.schedule[i].days[j].date) {
                            data.schedule[i].days[j].date = dateToIsoString(
                                new Date(data.schedule[i].days[j].date)
                            );
                        }
                    }
                }
            }
        }

        // Always sent, even empty: the api only falls back on the template's
        // instructions when the key is missing.
        const dataToSend = {
            ...data,
            other: data.other ?? "",
            other_interim: data.other_interim ?? "",
            dateStart: dateToIsoString(data.dateStart),
            dateEnd: dateToIsoString(data.dateEnd),
            ...(isPerimeterShown
                ? { perimeterId: toPerimeterIdPayload(perimeterChoice) }
                : {}),
        };

        ApiDatabase.postCreateMission(
            {
                dataToSend,
                idCompany,
                isDesktop: true,
                ...(selectedMissionTemplate?._id
                    ? { missionTemplateId: selectedMissionTemplate._id }
                    : {}),
            },
            () => {
                setData(resetMissionData);
                setSelectedMissionTemplate(null);
                handleNext();
                setIsSubmitLoading(false);
            },
            (err) => {
                console.log(err);
                const perimeterErrorKey = getMissionPerimeterErrorKey(err);
                if (perimeterErrorKey) {
                    handlePerimeterRefusal(
                        language.getValue(perimeterErrorKey)
                    );
                    return;
                }
                setIsSubmitLoading(false);
                toast.error(
                    err?.response?.data?.error || "Création impossible"
                );
            }
        );
    };

    const conditionMotif = (motifData) => {
        switch (motifData?.type) {
            case "1":
                return motifData.message !== "";
            case "2":
                return (
                    motifData.date !== "" &&
                    motifData.name !== "" &&
                    motifData.prenom !== ""
                );
            case "3":
                return (
                    motifData.message !== "" &&
                    motifData.name !== "" &&
                    motifData.prenom !== "" &&
                    motifData.poste !== ""
                );
            case "4":
                return motifData.message !== "";
            case "5":
                return motifData.message !== "";
            default:
                return false;
        }
    };

    const validateSchedule = () => {
        if (!data.isScheduleCheck) return true;

        if (data.schedule.length > 0) {
            let isValidSchedule = false;
            let isScheduleNotComplete = false;
            for (let i = 0; i < data.schedule.length; i++) {
                for (let j = 0; j < data.schedule[i]?.days?.length; j++) {
                    if (data.schedule[i].days[j].periods) {
                        if (
                            data.schedule[i].days[j].periods
                                .periode_one_start !== "" &&
                            data.schedule[i].days[j].periods
                                .periode_one_end !== ""
                        ) {
                            isValidSchedule = true;
                        } else {
                            isScheduleNotComplete = true;
                        }
                    } else {
                        isScheduleNotComplete = true;
                    }
                }
            }
            if (isValidSchedule) {
                if (!isScheduleNotComplete || data.isConfirmedSchedule) {
                    return true;
                }
                toggle("cM_confirmSchedule");
                return false;
            }
        }

        if (data.isScheduleRequired) {
            toast.error("Veuillez remplir le planning");
        } else {
            toast.error(
                "Veuillez remplir le planning ou retirer l'ajout d'un planning à la mission"
            );
        }
        return false;
    };

    const handlenxt = () => {
        switch (page) {
            case 1:
                if (!data.activity || !data.qualification) {
                    toast.error("Veuillez sélectionner secteur et qualification");
                    return;
                }
                handleNext();
                break;
            case 2:
                handleNext();
                break;
            case 3:
                if (data.motif === "") {
                    toast.error("Veuillez sélectionner un motif");
                    return;
                }
                if (!conditionMotif(data?.motif)) {
                    toast.error(
                        "Veuillez remplir les champs du motif sélectionné"
                    );
                    return;
                }
                handleNext();
                break;
            case 4:
                if (!hasLocation(data.location)) {
                    toast.error("Veuillez renseigner une adresse");
                    return;
                }
                if (
                    !data.dateStart ||
                    !data.dateEnd ||
                    !isDateValid
                ) {
                    toast.error(
                        "Veuillez renseigner les dates de la mission avec une période valide"
                    );
                    return;
                }
                handleNext();
                break;
            case 5:
                if ((
                    !data.dateStart ||
                    !data.dateEnd ||
                    !isDateValid
                )) {
                    toast.error(
                        "Veuillez renseigner les dates de la mission avec une période valide"
                    );
                    return;
                }
                if (validateSchedule()) {
                    handleNext();
                }
                break;
            case 6:
                if (!data.salary) {
                    toast.error("Veuillez renseigner un taux horaire");
                    return;
                }
                handleNext();
                break;
            case 7:
                handleNext();
                break;
            case 8:
                if (!data.contact_name || !data.contact_poste) {
                    toast.error("Veuillez compléter les informations responsable");
                    return;
                }
                handleNext();
                break;
            case DETAILS_PAGE:
                if (!canLeaveDetails) return;
                setPerimeterError("");
                handleNext();
                break;
            case 10:
                handleNext();
                break;
            default:
                break;
        }
    };

    const showFooter = page !== MISSION_END_PAGE;
    const showBack = page !== MISSION_END_PAGE && page !== firstPage;
    const isNextBlocked = page === DETAILS_PAGE && !canLeaveDetails;
    const stepIndex =
        page === MISSION_END_PAGE
            ? MISSION_STEPS_COUNT
            : getMissionStepIndex(page) + 1;

    if (!selectedMissionTemplate?._id && page !== MISSION_END_PAGE) {
        return null;
    }

    return (
        <>
            <PageForm>
                <div className="col-span-12 bg-white sticky z-10 top-16">
                    <CreateMissionSubNav />
                </div>
                <div className="col-span-full pb-24">
                    <div className="w-full mx-auto lg:w-[800px] px-6 pb-8">
                        {page === DETAILS_PAGE && isPerimeterShown && (
                            <div className="mb-6">
                                <PerimeterMissionStep
                                    options={perimeterOptions}
                                    value={perimeterChoice}
                                    onChange={handlePerimeterChange}
                                    error={perimeterError}
                                />
                            </div>
                        )}
                        <FormsCreate />
                    </div>
                </div>

                {showFooter && (
                    <div className="col-span-full">
                        <div className="fixed bottom-0 left-[80px] sidebar-expanded:left-64 2xl:left-64 right-0 bg-white border-t border-gray-100 z-20">
                            <div className="w-full mx-auto lg:w-[800px] px-6 py-4">
                                <div className="flex justify-between items-center gap-4">
                                    <Indicator
                                        formIndex={stepIndex}
                                        totalSteps={MISSION_STEPS_COUNT}
                                    />
                                    <div className="flex gap-3 shrink-0">
                                        {showBack ? (
                                            <Button
                                                size={"LG"}
                                                color={"SECONDARY"}
                                                onClick={handlePrev}
                                            >
                                                {language.getValue(
                                                    "missionCreation.wizard.back",
                                                    "Retour"
                                                )}
                                            </Button>
                                        ) : null}
                                        {isNextBlocked ? (
                                            <Button
                                                size={"LG"}
                                                color={"DISABLED"}
                                                disabled
                                            >
                                                {language.getValue(
                                                    "missionCreation.wizard.next",
                                                    "Suivant"
                                                )}
                                            </Button>
                                        ) : (
                                            <FooterButton
                                                oC1={handlenxt}
                                                oC2={handleSubmit}
                                                isLoading={isSubmitLoading}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </PageForm>
        </>
    );
};

export default CreateOfMission;
